import {
  ClipboardList,
  Play,
  BarChart3,
  Package,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import type { NetworkNode, NetworkNodeId } from "../types/login.types";
import styles from "./MesNetwork.module.css";

interface MesNetworkProps {
  nodes: NetworkNode[];
  className?: string;
}

const NODE_ICONS: Record<NetworkNodeId, typeof ClipboardList> = {
  PLAN: ClipboardList,
  EXECUTE: Play,
  MONITOR: BarChart3,
  MATERIAL: Package,
  QUALITY: ShieldCheck,
  MAINTENANCE: Wrench,
};

const CX = 200;
const CY = 190;
const HUB_R = 40;
const ORBIT_R = 122;
const NODE_R = 24;

/** 배경 별점 좌표 (고정 — 별자리 분위기) */
const STAR_FIELD: Array<{ x: number; y: number; r: number; o: number }> = [
  { x: 42, y: 48, r: 1.1, o: 0.55 },
  { x: 78, y: 96, r: 0.8, o: 0.4 },
  { x: 28, y: 170, r: 1.0, o: 0.45 },
  { x: 95, y: 250, r: 0.9, o: 0.35 },
  { x: 55, y: 310, r: 1.2, o: 0.5 },
  { x: 130, y: 40, r: 0.7, o: 0.35 },
  { x: 170, y: 70, r: 0.9, o: 0.45 },
  { x: 220, y: 38, r: 1.0, o: 0.5 },
  { x: 280, y: 55, r: 0.8, o: 0.4 },
  { x: 330, y: 90, r: 1.1, o: 0.55 },
  { x: 360, y: 140, r: 0.7, o: 0.35 },
  { x: 370, y: 200, r: 1.0, o: 0.45 },
  { x: 348, y: 270, r: 0.8, o: 0.4 },
  { x: 310, y: 330, r: 1.1, o: 0.5 },
  { x: 250, y: 360, r: 0.7, o: 0.35 },
  { x: 180, y: 355, r: 0.9, o: 0.4 },
  { x: 120, y: 340, r: 0.8, o: 0.4 },
  { x: 345, y: 38, r: 0.6, o: 0.3 },
  { x: 18, y: 240, r: 0.7, o: 0.35 },
  { x: 300, y: 180, r: 0.6, o: 0.3 },
  { x: 100, y: 150, r: 0.7, o: 0.35 },
  { x: 250, y: 110, r: 0.6, o: 0.32 },
];

function polar(angleDeg: number, radius: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.cos(rad),
    y: CY + radius * Math.sin(rad),
  };
}

function midPoint(
  a: { x: number; y: number },
  b: { x: number; y: number },
  t = 0.5
) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * MES 별자리 네트워크
 * - 중앙 MES 허브 + 6 모듈 궤도
 * - 인접 노드 링 + 교차 연결 → “여러 연결체가 하나가 됨”
 */
export function MesNetwork({ nodes, className }: MesNetworkProps) {
  const positions = nodes.map((n) => ({
    ...n,
    ...polar(n.angle, ORBIT_R),
  }));

  /** 인접 모듈 연결 (별자리 외곽 링) */
  const ringLinks = positions.map((p, i) => {
    const next = positions[(i + 1) % positions.length];
    return { from: p, to: next, key: `ring-${p.id}-${next.id}` };
  });

  /** 교차 연결 — 일부 대각/장거리 연결로 웹 느낌 */
  const crossPairs: Array<[number, number]> = [
    [0, 2],
    [1, 4],
    [2, 5],
    [0, 3],
  ];
  const crossLinks = crossPairs
    .filter(([a, b]) => a < positions.length && b < positions.length)
    .map(([a, b]) => ({
      from: positions[a],
      to: positions[b],
      key: `cross-${positions[a].id}-${positions[b].id}`,
    }));

  return (
    <svg
      className={[styles.network, className].filter(Boolean).join(" ")}
      viewBox="0 0 400 400"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="MES 시스템 네트워크: 모듈들이 별자리처럼 연결되어 하나로 통합됨"
    >
      <defs>
        <radialGradient id="mesHubFill" cx="40%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.55" />
          <stop offset="45%" stopColor="#1e3a8a" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0b1224" stopOpacity="1" />
        </radialGradient>
        <radialGradient id="mesNodeFill" cx="40%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#0a1428" stopOpacity="0.95" />
        </radialGradient>
        <linearGradient id="mesSpokeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#93c5fd" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#818cf8" stopOpacity="0.2" />
        </linearGradient>
        <filter id="mesHubGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="mesSoftGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 은은한 원형 궤도 (별자리 가이드) */}
      <circle className={styles.orbitGuide} cx={CX} cy={CY} r={ORBIT_R} />
      <circle
        className={styles.orbitGuideInner}
        cx={CX}
        cy={CY}
        r={ORBIT_R * 0.52}
      />

      {/* 배경 별 */}
      <g className={styles.starField} aria-hidden>
        {STAR_FIELD.map((s, i) => (
          <circle
            key={`star-${i}`}
            className={styles.star}
            cx={s.x}
            cy={s.y}
            r={s.r}
            style={{
              opacity: s.o,
              animationDelay: `${(i % 7) * 0.7}s`,
            }}
          />
        ))}
      </g>

      {/* 교차 연결 (얇은 원거리 연결) */}
      {crossLinks.map((link, i) => (
        <line
          key={link.key}
          className={styles.constellationCross}
          x1={link.from.x}
          y1={link.from.y}
          x2={link.to.x}
          y2={link.to.y}
          style={{ animationDelay: `${i * 0.9}s` }}
        />
      ))}

      {/* 외곽 링 — 모듈 간 연결 */}
      {ringLinks.map((link, i) => {
        const mid = midPoint(link.from, link.to, 0.5);
        return (
          <g key={link.key}>
            <line
              className={styles.constellationRing}
              x1={link.from.x}
              y1={link.from.y}
              x2={link.to.x}
              y2={link.to.y}
            />
            <circle
              className={styles.starlet}
              cx={mid.x}
              cy={mid.y}
              r={1.4}
              style={{ animationDelay: `${i * 0.5}s` }}
            />
          </g>
        );
      })}

      {/* MES 중심 스포크 */}
      {positions.map((p, i) => {
        const mid = midPoint({ x: CX, y: CY }, p, 0.58);
        return (
          <g key={`spoke-${p.id}`}>
            <line
              className={styles.spoke}
              x1={CX}
              y1={CY}
              x2={p.x}
              y2={p.y}
            />
            <line
              className={styles.spokeFlow}
              x1={CX}
              y1={CY}
              x2={p.x}
              y2={p.y}
              style={{ animationDelay: `${i * 0.55}s` }}
            />
            <circle
              className={styles.starlet}
              cx={mid.x}
              cy={mid.y}
              r={1.6}
              style={{ animationDelay: `${i * 0.65}s` }}
            />
          </g>
        );
      })}

      {/* 중앙 허브 */}
      <circle className={styles.hubRing} cx={CX} cy={CY} r={HUB_R + 16} />
      <circle
        className={`${styles.hubRing} ${styles.hubRingDelayed}`}
        cx={CX}
        cy={CY}
        r={HUB_R + 28}
      />
      <circle className={styles.hubCore} cx={CX} cy={CY} r={HUB_R} />
      {/* 허브 가장자리 작은 별점 */}
      {[0, 60, 120, 180, 240, 300].map((deg) => {
        const pt = polar(deg - 90, HUB_R);
        return (
          <circle
            key={`hub-dot-${deg}`}
            className={styles.hubSpark}
            cx={pt.x}
            cy={pt.y}
            r={1.5}
          />
        );
      })}
      <text className={styles.hubLabel} x={CX} y={CY}>
        MES
      </text>

      {/* 위성 노드 */}
      {positions.map((p, i) => {
        const Icon = NODE_ICONS[p.id];
        const labelY = p.y + NODE_R + 15;
        return (
          <g key={p.id}>
            <circle
              className={styles.nodePulse}
              cx={p.x}
              cy={p.y}
              r={NODE_R + 6}
              style={{ animationDelay: `${i * 0.4}s` }}
            />
            <circle className={styles.nodeCircle} cx={p.x} cy={p.y} r={NODE_R} />
            <foreignObject x={p.x - 11} y={p.y - 11} width={22} height={22}>
              <div className={styles.iconWrap}>
                <Icon size={15} strokeWidth={1.7} aria-hidden />
              </div>
            </foreignObject>
            <text className={styles.nodeLabel} x={p.x} y={labelY}>
              {p.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
